/**
 * @fileoverview Letterhead sample page preview
 *
 * One A4 page rendered at a reduced scale showing every letterhead zone in
 * place — header, footer and side bands — around sample letter text, so the
 * user can see the whole composition without exporting a document. The zone
 * artwork renders from the SAME `VariantFragments` HTML the exporters
 * consume (via `pickVariantForPage`), so what shows here is what prints.
 *
 * Optionally the preview doubles as a margin editor: pass `editableMargins`
 * (+ `marginValues` / `onMarginChange` / `onMarginCommit`) and each margin
 * becomes a draggable edge over the real letterhead, replacing the abstract
 * ruler diagram. Values are px in page coordinates (A4 = 794×1123 @ 96dpi),
 * matching how margins are stored.
 *
 * Used by:
 *   - Letterhead bundle cards (Settings → Branding → Letterheads)
 *   - Variant cards + their "Margins per layout" rows
 *   - The upload crop flow (pending extraction preview)
 *   - Document Margins (firm defaults) when a firm letterhead is set
 */

import DOMPurify, { type Config as DomPurifyConfig } from "dompurify";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { cn } from "@/utils/misc";
import {
  pickVariantForPage,
  sideCropGeometry,
  sideCropIsActive,
  EMPTY_SIDE_CROP,
  type DocumentPadding,
  type SideCrop,
  type VariantFragments,
} from "@/utils/branding/resolve-letterhead";
import {
  A4_PAGE_HEIGHT_PX,
  A4_PAGE_WIDTH_PX,
  computeTopBlockDims,
  type TopBlockPx,
} from "@/utils/export/exportHtmlDocument";
import {
  MARGIN_LIMITS,
  formatForUnit,
  type MarginUnit,
} from "@/utils/branding/marginUnits";
import {
  SAMPLE_LETTER_BODY_HTML,
  paddingToDiagramValues,
} from "@/utils/branding/samplePreview";
import {
  applyPageFitToPadding,
  fitZoneHtml,
  zonePageFit,
} from "@/utils/branding/pageFit";
import type { DiagramMarginKey, DiagramValues } from "./MarginsRulerDiagram";

/**
 * DOMPurify's default URI allow-list drops `blob:` image sources, which is
 * exactly what the upload flow previews before anything is stored. Allow
 * blob/data alongside http(s) so pending crops render in the sample page.
 */
const SANITIZE_OPTS: DomPurifyConfig = {
  ADD_DATA_URI_TAGS: ["img"],
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|blob|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

const A4_W = A4_PAGE_WIDTH_PX;
const A4_H = A4_PAGE_HEIGHT_PX;
/** Minimum body column width preserved while dragging side margins. */
const MIN_COLUMN_PX = 96;

/**
 * Load an image off-screen and resolve its rendered width when scaled to
 * exactly the A4 page height (same measurement PaginatedLetterPreview and
 * the export engine use for full-height side bands). 0 on failure.
 */
function measureSideWidthAtA4Height(url: string): Promise<number> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      if (!img.naturalHeight) {
        resolve(0);
        return;
      }
      resolve(Math.round(img.naturalWidth * (A4_H / img.naturalHeight)));
    };
    img.onerror = () => resolve(0);
    img.src = url;
  });
}

/** topBlock on-page dimensions (cropped aspect at the configured height). */
function measureTopBlockDims(
  url: string,
  heightPct: number,
  crop: SideCrop,
): Promise<TopBlockPx | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const visibleX = Math.max(0.1, 1 - crop.left - crop.right);
      const visibleY = Math.max(0.1, 1 - crop.top - crop.bottom);
      resolve(
        computeTopBlockDims(
          img.naturalWidth * visibleX,
          img.naturalHeight * visibleY,
          heightPct,
        ),
      );
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** Cropped topBlock image inside its fixed-size block (mirrors export). */
function TopBlockImage({
  src,
  alt,
  crop,
}: {
  src: string;
  alt: string;
  crop: SideCrop;
}) {
  if (!sideCropIsActive(crop)) {
    return (
      <img
        src={src}
        alt={alt}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "fill",
          display: "block",
        }}
      />
    );
  }
  const geo = sideCropGeometry(crop);
  return (
    <img
      src={src}
      alt={alt}
      style={{
        position: "absolute",
        display: "block",
        width: `${geo.imgWidthPct}%`,
        height: `${geo.imgHeightPct}%`,
        left: `-${geo.offsetLeftPct}%`,
        top: `-${geo.offsetTopPct}%`,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Margin drag handles
// ---------------------------------------------------------------------------

type HandleAxis = "x" | "y";
interface HandleSpec {
  key: DiagramMarginKey;
  axis: HandleAxis;
  from: "left" | "right" | "top" | "bottom";
  zone: "header" | "body" | "footer";
}

const HANDLES: HandleSpec[] = [
  { key: "headerTopPadding", axis: "y", from: "top", zone: "header" },
  { key: "headerLeftPadding", axis: "x", from: "left", zone: "header" },
  { key: "headerRightPadding", axis: "x", from: "right", zone: "header" },
  { key: "bodyTopPadding", axis: "y", from: "top", zone: "body" },
  { key: "bodyLeftPadding", axis: "x", from: "left", zone: "body" },
  { key: "bodyRightPadding", axis: "x", from: "right", zone: "body" },
  { key: "bodyBottomPadding", axis: "y", from: "bottom", zone: "body" },
  { key: "footerBottomPadding", axis: "y", from: "bottom", zone: "footer" },
  { key: "footerLeftPadding", axis: "x", from: "left", zone: "footer" },
  { key: "footerRightPadding", axis: "x", from: "right", zone: "footer" },
];

const HANDLE_LABEL: Record<DiagramMarginKey, string> = {
  headerTopPadding: "Header top",
  headerLeftPadding: "Header left",
  headerRightPadding: "Header right",
  bodyTopPadding: "Body top",
  bodyLeftPadding: "Body left",
  bodyRightPadding: "Body right",
  bodyBottomPadding: "Body bottom",
  footerBottomPadding: "Footer bottom",
  footerLeftPadding: "Footer left",
  footerRightPadding: "Footer right",
};

export interface LetterheadSamplePreviewProps {
  /** Ordered fragments (highest priority first) — same list the export walks. */
  fragments: VariantFragments[];
  /** Resolved padding driving the layout (live values while dragging). */
  padding: DocumentPadding;
  /** Page shown (drives per-zone page rules). Default 1. */
  pageIndex?: number;
  /** Rendered page width in CSS px. Default 300. */
  width?: number;
  /** Caption above the page (e.g. "Page 1"). */
  label?: string;
  className?: string;
  /** Enable draggable margin edges over the page. */
  editableMargins?: boolean;
  /** Display unit for the drag readout. Default "cm". */
  unit?: MarginUnit;
  /** Current margin values (page px). Required when `editableMargins`. */
  marginValues?: DiagramValues;
  /** Fires continuously while a margin edge is dragged. */
  onMarginChange?: (key: DiagramMarginKey, px: number) => void;
  /** Fires once on release. */
  onMarginCommit?: (key: DiagramMarginKey, px: number) => void;
  disabled?: boolean;
}

export function LetterheadSamplePreview({
  fragments,
  padding,
  pageIndex = 1,
  width = 300,
  label,
  className,
  editableMargins = false,
  unit = "cm",
  marginValues,
  onMarginChange,
  onMarginCommit,
  disabled,
}: LetterheadSamplePreviewProps) {
  const scale = width / A4_W;
  const height = Math.round(A4_H * scale);

  // --- Per-page zone winners (same picker the exporters use) -----------
  const headerVariant = pickVariantForPage(fragments, "header", pageIndex);
  const footerVariant = pickVariantForPage(fragments, "footer", pageIndex);
  const leftVariant = pickVariantForPage(fragments, "leftSide", pageIndex);
  const rightVariant = pickVariantForPage(fragments, "rightSide", pageIndex);

  const headerHtml = useMemo(
    () =>
      headerVariant?.headerHtml
        ? DOMPurify.sanitize(headerVariant.headerHtml, SANITIZE_OPTS)
        : "",
    [headerVariant?.headerHtml],
  );
  const footerHtml = useMemo(
    () =>
      footerVariant?.footerHtml
        ? DOMPurify.sanitize(footerVariant.footerHtml, SANITIZE_OPTS)
        : "",
    [footerVariant?.footerHtml],
  );

  // Page-fit header/footer slots ignore the zone margins and lay out at
  // page width (mirrors the export's slot builders).
  const pageFit = zonePageFit(headerVariant, footerVariant);
  const slotPadding = applyPageFitToPadding(padding, pageFit);

  // --- Side band measurement -------------------------------------------
  const leftIsTopBlock = leftVariant?.leftSideFit === "topBlock";
  const rightIsTopBlock = rightVariant?.rightSideFit === "topBlock";
  const leftBandUrl = !leftIsTopBlock
    ? (leftVariant?.leftSideImageUrl ?? null)
    : null;
  const rightBandUrl = !rightIsTopBlock
    ? (rightVariant?.rightSideImageUrl ?? null)
    : null;
  const leftBlockUrl = leftIsTopBlock
    ? (leftVariant?.leftSideImageUrl ?? null)
    : null;
  const rightBlockUrl = rightIsTopBlock
    ? (rightVariant?.rightSideImageUrl ?? null)
    : null;

  const [leftBandW, setLeftBandW] = useState(0);
  const [rightBandW, setRightBandW] = useState(0);
  const [leftBlock, setLeftBlock] = useState<TopBlockPx | null>(null);
  const [rightBlock, setRightBlock] = useState<TopBlockPx | null>(null);

  useEffect(() => {
    if (!leftBandUrl) {
      setLeftBandW(0);
      return;
    }
    let cancelled = false;
    measureSideWidthAtA4Height(leftBandUrl).then((w) => {
      if (!cancelled) setLeftBandW(w);
    });
    return () => {
      cancelled = true;
    };
  }, [leftBandUrl]);

  useEffect(() => {
    if (!rightBandUrl) {
      setRightBandW(0);
      return;
    }
    let cancelled = false;
    measureSideWidthAtA4Height(rightBandUrl).then((w) => {
      if (!cancelled) setRightBandW(w);
    });
    return () => {
      cancelled = true;
    };
  }, [rightBandUrl]);

  const leftBlockPct = leftVariant?.leftSideTopBlockHeightPct ?? 0;
  const leftBlockCrop = leftVariant?.leftSideCrop ?? EMPTY_SIDE_CROP;
  useEffect(() => {
    if (!leftBlockUrl) {
      setLeftBlock(null);
      return;
    }
    let cancelled = false;
    measureTopBlockDims(leftBlockUrl, leftBlockPct, leftBlockCrop).then(
      (dims) => {
        if (!cancelled) setLeftBlock(dims);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [leftBlockUrl, leftBlockPct, leftBlockCrop]);

  const rightBlockPct = rightVariant?.rightSideTopBlockHeightPct ?? 0;
  const rightBlockCrop = rightVariant?.rightSideCrop ?? EMPTY_SIDE_CROP;
  useEffect(() => {
    if (!rightBlockUrl) {
      setRightBlock(null);
      return;
    }
    let cancelled = false;
    measureTopBlockDims(rightBlockUrl, rightBlockPct, rightBlockCrop).then(
      (dims) => {
        if (!cancelled) setRightBlock(dims);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [rightBlockUrl, rightBlockPct, rightBlockCrop]);

  // --- Header / footer zone heights (measured, page px) -----------------
  const headerBoxRef = useRef<HTMLDivElement | null>(null);
  const footerBoxRef = useRef<HTMLDivElement | null>(null);
  const [headerZoneH, setHeaderZoneH] = useState(0);
  const [footerZoneH, setFooterZoneH] = useState(0);

  useEffect(() => {
    const el = headerBoxRef.current;
    if (!headerHtml || !el) {
      setHeaderZoneH(0);
      return;
    }
    const measure = () => setHeaderZoneH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const cleanups: Array<() => void> = [];
    for (const img of Array.from(el.querySelectorAll("img"))) {
      if (img.complete) continue;
      img.addEventListener("load", measure);
      img.addEventListener("error", measure);
      cleanups.push(() => {
        img.removeEventListener("load", measure);
        img.removeEventListener("error", measure);
      });
    }
    return () => {
      ro.disconnect();
      for (const cleanup of cleanups) cleanup();
    };
  }, [
    headerHtml,
    slotPadding.headerTop,
    slotPadding.headerLeft,
    slotPadding.headerRight,
  ]);

  useEffect(() => {
    const el = footerBoxRef.current;
    if (!footerHtml || !el) {
      setFooterZoneH(0);
      return;
    }
    const measure = () => setFooterZoneH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const cleanups: Array<() => void> = [];
    for (const img of Array.from(el.querySelectorAll("img"))) {
      if (img.complete) continue;
      img.addEventListener("load", measure);
      img.addEventListener("error", measure);
      cleanups.push(() => {
        img.removeEventListener("load", measure);
        img.removeEventListener("error", measure);
      });
    }
    return () => {
      ro.disconnect();
      for (const cleanup of cleanups) cleanup();
    };
  }, [
    footerHtml,
    slotPadding.footerBottom,
    slotPadding.footerLeft,
    slotPadding.footerRight,
  ]);

  // --- Geometry (page px) ----------------------------------------------
  const headerLeftInset = Math.max(leftBandW, leftBlock?.widthPx ?? 0);
  const headerRightInset = Math.max(rightBandW, rightBlock?.widthPx ?? 0);
  const bodyTopEdge = headerHtml ? headerZoneH : 0;
  const bodyBottomEdge = A4_H - (footerHtml ? footerZoneH : 0);

  // topBlock wrap spacers — approximate the export's wrap math so the
  // sample text visibly flows beside the block and full-width below it.
  const leftSpacer = leftBlock
    ? {
        w: Math.max(0, leftBlock.widthPx - leftBandW - padding.bodyLeft + 12),
        h: Math.max(0, leftBlock.heightPx - bodyTopEdge - padding.bodyTop + 12),
      }
    : null;
  const rightSpacer = rightBlock
    ? {
        w: Math.max(
          0,
          rightBlock.widthPx - rightBandW - padding.bodyRight + 12,
        ),
        h: Math.max(
          0,
          rightBlock.heightPx - bodyTopEdge - padding.bodyTop + 12,
        ),
      }
    : null;

  // --- Margin dragging ---------------------------------------------------
  const pageRef = useRef<HTMLDivElement | null>(null);
  const [activeKey, setActiveKey] = useState<DiagramMarginKey | null>(null);
  const liveRef = useRef<{ key: DiagramMarginKey; px: number } | null>(null);
  const values = marginValues ?? paddingToDiagramValues(padding);

  const zoneBox = useCallback(
    (zone: HandleSpec["zone"]) =>
      zone === "header"
        ? { top: 0, bottom: Math.max(bodyTopEdge, 80) }
        : zone === "body"
          ? { top: bodyTopEdge, bottom: bodyBottomEdge }
          : { top: Math.min(bodyBottomEdge, A4_H - 60), bottom: A4_H },
    [bodyTopEdge, bodyBottomEdge],
  );

  /** Left/right inset (page px) x-axis handle positions measure from. */
  const zoneXInset = useCallback(
    (zone: HandleSpec["zone"], from: "left" | "right") => {
      if (zone === "header")
        return from === "left" ? headerLeftInset : headerRightInset;
      return from === "left" ? leftBandW : rightBandW;
    },
    [headerLeftInset, headerRightInset, leftBandW, rightBandW],
  );

  const clampValue = useCallback(
    (h: HandleSpec, raw: number) => {
      const max: number = MARGIN_LIMITS.px.max;
      const limit: number =
        h.axis === "x"
          ? (() => {
              const otherKey = (
                h.from === "left"
                  ? h.key.replace("Left", "Right")
                  : h.key.replace("Right", "Left")
              ) as DiagramMarginKey;
              const avail =
                A4_W - zoneXInset(h.zone, "left") - zoneXInset(h.zone, "right");
              return Math.min(max, avail - values[otherKey] - MIN_COLUMN_PX);
            })()
          : max;
      return Math.max(0, Math.min(limit, Math.round(raw)));
    },
    [values, zoneXInset],
  );

  const beginDrag = (h: HandleSpec) => (e: React.PointerEvent) => {
    if (disabled || !editableMargins) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setActiveKey(h.key);
    liveRef.current = { key: h.key, px: values[h.key] };
  };

  const moveDrag = (h: HandleSpec) => (e: React.PointerEvent) => {
    if (activeKey !== h.key || !pageRef.current) return;
    const rect = pageRef.current.getBoundingClientRect();
    const s = rect.width / A4_W;
    const raw: number =
      h.axis === "x"
        ? (() => {
            const x = (e.clientX - rect.left) / s;
            return h.from === "left"
              ? x - zoneXInset(h.zone, "left")
              : A4_W - zoneXInset(h.zone, "right") - x;
          })()
        : (() => {
            const y = (e.clientY - rect.top) / s;
            const box = zoneBox(h.zone);
            return h.from === "top" ? y - box.top : box.bottom - y;
          })();
    const px = clampValue(h, raw);
    if (liveRef.current?.px === px) return;
    liveRef.current = { key: h.key, px };
    onMarginChange?.(h.key, px);
  };

  const endDrag = (h: HandleSpec) => (e: React.PointerEvent) => {
    if (activeKey !== h.key) return;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      // already released
    }
    const px = liveRef.current?.px ?? values[h.key];
    setActiveKey(null);
    liveRef.current = null;
    onMarginCommit?.(h.key, px);
  };

  const sx = (px: number) => px * scale;
  const readout = activeKey
    ? `${HANDLE_LABEL[activeKey]}: ${formatForUnit(liveRef.current?.px ?? values[activeKey], unit)} ${unit}`
    : null;

  const visibleHandles = editableMargins
    ? HANDLES.filter((h) => {
        // A page-fit zone sits where the firm's letterhead put it — its
        // margins don't apply, so it gets no handles.
        if (h.zone === "header")
          return !!headerHtml && pageFit.headerTop === null;
        if (h.zone === "footer")
          return !!footerHtml && pageFit.footerBottom === null;
        return true;
      })
    : [];

  const hasAnyZone =
    !!headerHtml ||
    !!footerHtml ||
    !!leftBandUrl ||
    !!rightBandUrl ||
    !!leftBlockUrl ||
    !!rightBlockUrl;

  return (
    <div
      className={cn(
        "flex select-none flex-col items-center gap-1.5",
        className,
      )}
    >
      {label && (
        <span className="text-[10px] font-medium uppercase tracking-wide text-primary/40">
          {label}
        </span>
      )}
      <div
        ref={pageRef}
        className="relative shrink-0 overflow-hidden border border-border/60 bg-white shadow-sm"
        style={{ width, height }}
        data-testid="letterhead-sample-preview"
      >
        {/* The page renders at full A4 size and is scaled down as a whole,
         *  so every measurement below is in real export px. */}
        <div
          style={{
            width: A4_W,
            height: A4_H,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            position: "relative",
            backgroundColor: "white",
          }}
        >
          {/* Full-height side bands */}
          {leftBandUrl && leftBandW > 0 && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: leftBandW,
                height: A4_H,
                lineHeight: 0,
                fontSize: 0,
              }}
            >
              <img
                src={leftBandUrl}
                alt="Left side letterhead"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "fill",
                  display: "block",
                }}
              />
            </div>
          )}
          {rightBandUrl && rightBandW > 0 && (
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: rightBandW,
                height: A4_H,
                lineHeight: 0,
                fontSize: 0,
              }}
            >
              <img
                src={rightBandUrl}
                alt="Right side letterhead"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "fill",
                  display: "block",
                }}
              />
            </div>
          )}

          {/* Partial-height ("topBlock") side blocks */}
          {leftBlockUrl && leftBlock && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: leftBlock.widthPx,
                height: leftBlock.heightPx,
                overflow: "hidden",
                lineHeight: 0,
                fontSize: 0,
              }}
            >
              <TopBlockImage
                src={leftBlockUrl}
                alt="Left side letterhead"
                crop={leftBlockCrop}
              />
            </div>
          )}
          {rightBlockUrl && rightBlock && (
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: rightBlock.widthPx,
                height: rightBlock.heightPx,
                overflow: "hidden",
                lineHeight: 0,
                fontSize: 0,
              }}
            >
              <TopBlockImage
                src={rightBlockUrl}
                alt="Right side letterhead"
                crop={rightBlockCrop}
              />
            </div>
          )}

          {/* Header */}
          {headerHtml && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: headerLeftInset,
                right: headerRightInset,
                overflow: "hidden",
              }}
            >
              <div
                ref={headerBoxRef}
                style={{
                  padding: `${slotPadding.headerTop}px ${slotPadding.headerRight}px 0 ${slotPadding.headerLeft}px`,
                }}
                // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- headerHtml is DOMPurify.sanitize'd (SANITIZE_OPTS) in the headerHtml memo above; fitZoneHtml only adds a numeric-only wrapper
                dangerouslySetInnerHTML={{
                  __html: fitZoneHtml(
                    headerHtml,
                    pageFit.headerTop,
                    headerLeftInset,
                  ),
                }}
              />
            </div>
          )}

          {/* Sample body */}
          <div
            style={{
              position: "absolute",
              top: bodyTopEdge,
              left: leftBandW,
              right: rightBandW,
              bottom: A4_H - bodyBottomEdge,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: `${padding.bodyTop}px ${padding.bodyRight}px 0 ${padding.bodyLeft}px`,
                fontFamily: '"Times New Roman", Georgia, serif',
                fontSize: 15,
                lineHeight: 1.5,
                color: "#3a3a3a",
              }}
            >
              {leftSpacer && leftSpacer.w > 0 && leftSpacer.h > 0 && (
                <div
                  style={{
                    float: "left",
                    width: leftSpacer.w,
                    height: leftSpacer.h,
                    marginRight: 12,
                  }}
                  aria-hidden
                />
              )}
              {rightSpacer && rightSpacer.w > 0 && rightSpacer.h > 0 && (
                <div
                  style={{
                    float: "right",
                    width: rightSpacer.w,
                    height: rightSpacer.h,
                    marginLeft: 12,
                  }}
                  aria-hidden
                />
              )}
              <div
                // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- SAMPLE_LETTER_BODY_HTML is a compile-time constant in utils/branding/samplePreview.ts, no external input
                dangerouslySetInnerHTML={{ __html: SAMPLE_LETTER_BODY_HTML }}
              />
            </div>
          </div>

          {/* Footer */}
          {footerHtml && (
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: leftBandW,
                right: rightBandW,
                overflow: "hidden",
              }}
            >
              <div
                ref={footerBoxRef}
                style={{
                  padding: `0 ${slotPadding.footerRight}px ${slotPadding.footerBottom}px ${slotPadding.footerLeft}px`,
                }}
                // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- footerHtml is DOMPurify.sanitize'd (SANITIZE_OPTS) in the footerHtml memo above; fitZoneHtml only adds a numeric-only wrapper
                dangerouslySetInnerHTML={{
                  __html: fitZoneHtml(
                    footerHtml,
                    pageFit.footerBottom,
                    leftBandW,
                  ),
                }}
              />
            </div>
          )}

          {!hasAnyZone && (
            <div
              style={{
                position: "absolute",
                top: 8,
                left: 0,
                right: 0,
                textAlign: "center",
                fontSize: 20,
                color: "rgba(0,0,0,0.3)",
              }}
            >
              No letterhead on this page
            </div>
          )}
        </div>

        {/* Margin drag handles — rendered over the scaled page in CSS px. */}
        {visibleHandles.map((h) => {
          const box = zoneBox(h.zone);
          const v = values[h.key];
          const isActive = activeKey === h.key;
          const style: CSSProperties =
            h.axis === "x"
              ? {
                  top: sx(box.top),
                  height: sx(box.bottom - box.top),
                  left:
                    h.from === "left"
                      ? sx(zoneXInset(h.zone, "left") + v)
                      : undefined,
                  right:
                    h.from === "right"
                      ? sx(zoneXInset(h.zone, "right") + v)
                      : undefined,
                  width: 9,
                  transform:
                    h.from === "left" ? "translateX(-50%)" : "translateX(50%)",
                }
              : {
                  left: sx(zoneXInset(h.zone, "left")),
                  right: sx(zoneXInset(h.zone, "right")),
                  top: h.from === "top" ? sx(box.top + v) : undefined,
                  bottom:
                    h.from === "bottom" ? sx(A4_H - box.bottom + v) : undefined,
                  height: 9,
                  transform:
                    h.from === "top" ? "translateY(-50%)" : "translateY(50%)",
                };
          return (
            <button
              key={h.key}
              type="button"
              aria-label={`Drag to set ${HANDLE_LABEL[h.key].toLowerCase()} margin`}
              title={HANDLE_LABEL[h.key]}
              data-testid={`sample-margin-handle-${h.key}`}
              disabled={disabled}
              onPointerDown={beginDrag(h)}
              onPointerMove={moveDrag(h)}
              onPointerUp={endDrag(h)}
              onPointerCancel={endDrag(h)}
              className={cn(
                "absolute z-10 touch-none rounded-sm transition-colors",
                disabled
                  ? "cursor-default"
                  : h.axis === "x"
                    ? "cursor-ew-resize hover:bg-primary/20"
                    : "cursor-ns-resize hover:bg-primary/20",
                isActive ? "bg-primary/30" : "bg-primary/[0.07]",
              )}
              style={style}
            />
          );
        })}

        {/* Dashed guides marking the current margin edges (edit mode). */}
        {editableMargins &&
          visibleHandles.map((h) => {
            const box = zoneBox(h.zone);
            const v = values[h.key];
            const style: CSSProperties =
              h.axis === "x"
                ? {
                    top: sx(box.top),
                    height: sx(box.bottom - box.top),
                    left:
                      h.from === "left"
                        ? sx(zoneXInset(h.zone, "left") + v)
                        : undefined,
                    right:
                      h.from === "right"
                        ? sx(zoneXInset(h.zone, "right") + v)
                        : undefined,
                    width: 0,
                    borderLeft: "1px dashed rgba(56, 67, 124, 0.45)",
                  }
                : {
                    left: sx(zoneXInset(h.zone, "left")),
                    right: sx(zoneXInset(h.zone, "right")),
                    top: h.from === "top" ? sx(box.top + v) : undefined,
                    bottom:
                      h.from === "bottom"
                        ? sx(A4_H - box.bottom + v)
                        : undefined,
                    height: 0,
                    borderTop: "1px dashed rgba(56, 67, 124, 0.45)",
                  };
            return (
              <div
                key={`guide-${h.key}`}
                className="pointer-events-none absolute"
                style={style}
                aria-hidden
              />
            );
          })}

        {/* Live readout while dragging */}
        {readout && (
          <div className="pointer-events-none absolute left-1/2 top-1.5 z-20 -translate-x-1/2 whitespace-nowrap rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground shadow">
            {readout}
          </div>
        )}
      </div>
    </div>
  );
}
